import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { PracticeField } from "./PracticeField";
import { RelationInputs } from "./PracticeRelations";
import { practiceApi, euro } from "../../lib/practiceApi";

const groups = [
  ["Pratica", ["summary", "name", "type", "type_other", "practice_number", "status", "opening_date", "responsible", "municipality"]],
  ["Titoli edilizi e lavori", ["permit_issue_date", "work_start_date", "work_end_date"]],
  ["SCIA, paesaggistica e solleciti", ["scia_submission_date", "landscape_constraint", "landscape_procedure", "superintendence_submission_date", "reminder_sent_date"]],
  ["Onorario dello studio", ["quote_amount", "agreed_amount"]],
  ["Importo lavori, direzione lavori e coordinamento", ["works_amount", "works_direction_pct", "coordination_pct"]],
  ["Note", ["notes"]],
];

const today = () => new Date().toLocaleDateString("en-CA");
const fee = (works, pct) => works && pct ? Math.round(Number(works) * Number(pct)) / 100 : 0;

export const PracticeCreateDialog = ({ open, onOpenChange, clients, sites, onCreated }) => {
  const [draft, setDraft] = useState({ opening_date: today(), status: "Da avviare" });
  const [relations, setRelations] = useState({});
  const [busy, setBusy] = useState(false);
  const [isSite, setIsSite] = useState(false);
  const [error, setError] = useState("");
  const change = (field, value) => setDraft(d => ({ ...d, [field]: value }));
  const directionFee = fee(draft.works_amount, draft.works_direction_pct);
  const coordinationFee = fee(draft.works_amount, draft.coordination_pct);
  const total = Number(draft.agreed_amount || 0) + directionFee + coordinationFee;
  function visible(key) { return key !== "type_other" || draft.type === "altro"; }
  async function submit(e) {
    e.preventDefault(); setError("");
    if (isSite && !relations.site_id && !relations.client_id) return setError("Per creare il cantiere collegato seleziona prima il committente.");
    setBusy(true);
    const data = Object.fromEntries(Object.entries({ ...draft, ...relations }).filter(([, value]) => value !== "" && value != null));
    try {
      const result = await practiceApi("/practices/dossier", { method: "POST", body: JSON.stringify({ data }) });
      if (isSite) {
        await practiceApi(`/practices/${result.practice.id}/site`, { method: "POST", body: JSON.stringify({
          data: relations.site_id ? { mode: "flag" } : { mode: "create", name: draft.summary || draft.name || "", municipality: draft.municipality || "" },
        }) });
      }
      onCreated(result.practice.id);
    }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  return <Dialog open={open} onOpenChange={value => { if (!busy) { onOpenChange(value); if (value) { setDraft({ opening_date: today(), status: "Da avviare" }); setRelations({}); setIsSite(false); setError(""); } } }}>
    <DialogContent className="practice-dialog practice-dialog-wide practice-workspace" data-testid="practice-create-dialog" closeTestId="practice-create-close">
      <DialogTitle data-testid="practice-create-title">Nuova pratica</DialogTitle>
      <DialogDescription data-testid="practice-create-description">Registra il fascicolo: i campi non disponibili si compilano in seguito, anche tramite lettura AI dei documenti.</DialogDescription>
      <form onSubmit={submit}>
        <fieldset className="practice-fieldset"><legend>Cliente e cantiere</legend><RelationInputs value={relations} onChange={setRelations} clients={clients} sites={sites} prefix="practice-create" />
          <label className="site-flag-toggle" data-testid="practice-create-is-site">
            <input type="checkbox" checked={isSite} onChange={e => setIsSite(e.target.checked)} />
            <span><strong>Questa pratica è un cantiere</strong><small>{relations.site_id ? "Il cantiere selezionato resta collegato e la pratica viene segnata come cantiere." : "Crea automaticamente il cantiere collegato nella sezione Cantieri (serve il committente)."}</small></span>
          </label>
        </fieldset>
        {groups.map(([title, keys]) => <fieldset key={title} className="practice-fieldset"><legend>{title}</legend><div className="practice-form-grid">
          {keys.filter(visible).map(key => <PracticeField key={key} fieldKey={key} prefix="practice-create-field" value={draft[key]} onChange={change}>
            {key === "quote_amount" && <Button type="button" variant="outline" className="ghost use-quote" data-testid="practice-create-use-quote" onClick={() => change("agreed_amount", draft.quote_amount || "")}>Usa come importo concordato</Button>}
          </PracticeField>)}
        </div></fieldset>)}
        <div className="create-hint muted">I termini di legge (inizio lavori 365 giorni dal PdC, fine lavori 3 anni dal PdC, SCIA 3 anni dalla presentazione) e la scadenza del sollecito alla Soprintendenza (60 gg Semplificata / 90 gg Ordinaria) sono calcolati automaticamente dal sistema.</div>
        <div className="create-preview" data-testid="practice-create-preview">
          <div><span>Compenso direzione lavori</span><strong data-testid="practice-create-direction-fee">{euro(directionFee)}</strong></div>
          <div><span>Compenso coordinamento</span><strong data-testid="practice-create-coordination-fee">{euro(coordinationFee)}</strong></div>
          <div><span>Totale compenso</span><strong data-testid="practice-create-total-fee">{euro(total)}</strong></div>
        </div>
        {error && <div className="error" role="alert" data-testid="practice-create-error">{error}</div>}
        <Button type="submit" className="primary wide" disabled={busy} data-testid="practice-create-submit">{busy ? "Creazione…" : "Crea fascicolo"}<ArrowUpRight size={16} /></Button>
      </form>
    </DialogContent>
  </Dialog>;
};
